// components/vendor/VendorOnboarding.jsx
import React, { useState } from 'react';
import { 
  Store, 
  Package, 
  TrendingUp, 
  CheckCircle, 
  AlertCircle,
  Upload,
  Plus,
  X,
  ArrowRight,
  ArrowLeft,
  Loader,
  DollarSign,
  Users,
  Truck,
  Clock,
  Shield,
  HelpCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTrust } from '../../context/TrustContext';
import { useNotification } from '../../context/NotificationContext';
import Modal from '../common/Modal';
import { updateTerminology } from '../../utils/memberLanguage';

const VendorOnboarding = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userTrust } = useTrust();
  const { addNotification } = useNotification();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    businessName: '',
    businessType: '',
    contactEmail: user?.email || '',
    contactPhone: '',
    location: {
      city: 'Sacramento',
      state: 'CA',
      serviceRadius: '25'
    },
    businessLicense: '',
    insuranceInfo: '',
    yearEstablished: '',
    
    // Step 2: Products & Services
    products: [],
    minimumOrderValue: '',
    deliveryOptions: {
      pickup: true,
      delivery: false,
      shipping: false
    },
    pricingTiers: [],
    jobOpportunities: [],
    bulkDiscounts: true,
    
    // Step 3: Terms & Launch
    acceptTerms: false,
    acceptPlatformFees: false,
    paymentMethod: 'bank_transfer',
    initialInventory: []
  });

  // Business type options
  const businessTypes = [
    { value: 'farm', label: 'Farm/Agricultural Producer', icon: '🌾' },
    { value: 'food_manufacturer', label: 'Food Manufacturer', icon: '🏭' },
    { value: 'artisan', label: 'Artisan/Handmade Products', icon: '🎨' },
    { value: 'distributor', label: 'Wholesale Distributor', icon: '📦' },
    { value: 'retail', label: 'Retail Business', icon: '🏪' },
    { value: 'service', label: 'Service Provider', icon: '🛠️' }
  ];

  // Product categories
  const productCategories = [
    'Groceries',
    'Household',
    'Personal Care',
    'Electronics',
    'Home & Garden',
    'Health & Wellness',
    'Arts & Crafts',
    'Baby & Kids'
  ];

  // Validation functions
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    if (!formData.businessType) {
      newErrors.businessType = 'Please select a business type';
    }
    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = 'Contact phone is required';
    }
    if (userTrust?.score < 3.5) {
      newErrors.trust = 'Vendors must have a trust score of 3.5 or higher';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (formData.products.length === 0) {
      newErrors.products = 'Add at least one product or service';
    }
    if (!formData.minimumOrderValue || parseFloat(formData.minimumOrderValue) <= 0) {
      newErrors.minimumOrderValue = 'Please set a minimum order value';
    }
    if (formData.pricingTiers.length === 0) {
      newErrors.pricingTiers = 'Add at least one pricing tier';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the vendor terms';
    }
    if (!formData.acceptPlatformFees) {
      newErrors.acceptPlatformFees = 'You must accept the platform fee structure';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle step navigation
  const handleNext = () => {
    let isValid = false;
    
    switch (currentStep) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      setCurrentStep(currentStep + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep3()) return;
    
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Save vendor data to localStorage for demo
      const vendorData = {
        id: `vendor_${Date.now()}`,
        ...formData,
        status: 'active',
        createdAt: new Date().toISOString(),
        trustScore: userTrust?.score || 3.5,
        createdBy: user?.id || 'user123'
      };
      
      // Store in localStorage
      const existingVendors = JSON.parse(localStorage.getItem('vendors') || '[]');
      existingVendors.push(vendorData);
      localStorage.setItem('vendors', JSON.stringify(existingVendors));
      
      // Update member to vendor status
      const updatedUser = {
        ...user,
        isVendor: true,
        vendorId: vendorData.id
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Show success notification
      addNotification({
        type: 'success',
        title: 'Welcome to UniLedge!',
        message: 'Your vendor account has been created successfully.',
        showToast: true
      });
      
      // Call success callback
      if (onSuccess) {
        onSuccess(vendorData);
      } else {
        // Navigate to vendor dashboard
        navigate('/vendor/dashboard');
      }
      
    } catch (error) {
      setErrors({ submit: 'Failed to create vendor account. Please try again.' });
      addNotification({
        type: 'error',
        title: 'Registration Failed',
        message: 'There was an error creating your vendor account.',
        showToast: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Add product
  const addProduct = () => {
    const newProduct = {
      id: Date.now(),
      name: '',
      category: '',
      description: '',
      basePrice: '',
      unit: 'each'
    };
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, newProduct]
    }));
  };

  const updateProduct = (productId, field, value) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map(p => 
        p.id === productId ? { ...p, [field]: value } : p
      )
    }));
  };

  const removeProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter(p => p.id !== productId)
    }));
  };

  // Add pricing tier
  const addPricingTier = () => {
    const newTier = {
      id: Date.now(),
      minQuantity: '',
      maxQuantity: '',
      discount: ''
    };
    setFormData(prev => ({
      ...prev,
      pricingTiers: [...prev.pricingTiers, newTier]
    }));
  };

  const updatePricingTier = (tierId, field, value) => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: prev.pricingTiers.map(t => 
        t.id === tierId ? { ...t, [field]: value } : t
      )
    }));
  };

  const removePricingTier = (tierId) => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: prev.pricingTiers.filter(t => t.id !== tierId)
    }));
  };

  // Progress indicator
  const steps = [
    { number: 1, label: 'Basic Info' },
    { number: 2, label: 'Products' },
    { number: 3, label: 'Launch' }
  ];

  const renderProgressBar = () => (
    <div className="mb-6">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  currentStep >= step.number
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {currentStep > step.number ? (
                  <CheckCircle size={20} />
                ) : (
                  step.number
                )}
              </div>
              <span className={`ml-2 text-sm ${
                currentStep >= step.number ? 'text-blue-600 font-medium' : 'text-gray-500'
              }`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-1 mx-4 ${
                currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <Store size={48} className="mx-auto text-blue-600 mb-3" />
        <h2 className="text-2xl font-bold">Tell us about your business</h2>
        <p className="text-gray-600 mt-2">
          Join thousands of vendors connecting with local community members
        </p>
      </div>

      {/* Trust score check */}
      {userTrust?.score < 3.5 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-yellow-600 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-sm text-yellow-800 font-medium">Trust Score Requirement</p>
            <p className="text-sm text-yellow-700 mt-1">
              Vendors need a trust score of 3.5 or higher. Your current score is {userTrust?.score}.
              Complete more groups to increase your score.
            </p>
          </div>
        </div>
      )}

      {/* Business Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Business Name *
        </label>
        <input
          type="text"
          value={formData.businessName}
          onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.businessName ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Your Business Name"
        />
        {errors.businessName && (
          <p className="text-red-500 text-sm mt-1">{errors.businessName}</p>
        )}
      </div>

      {/* Business Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Business Type *
        </label>
        <div className="grid grid-cols-2 gap-3">
          {businessTypes.map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, businessType: type.value }))}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                formData.businessType === type.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <span className="text-2xl mr-2">{type.icon}</span>
                <span className="text-sm font-medium">{type.label}</span>
              </div>
            </button>
          ))}
        </div>
        {errors.businessType && (
          <p className="text-red-500 text-sm mt-1">{errors.businessType}</p>
        )}
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contact Email
          </label>
          <input
            type="email"
            value={formData.contactEmail}
            onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contact Phone *
          </label>
          <input
            type="tel"
            value={formData.contactPhone}
            onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.contactPhone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="(555) 123-4567"
          />
          {errors.contactPhone && (
            <p className="text-red-500 text-sm mt-1">{errors.contactPhone}</p>
          )}
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Service Area
        </label>
        <div className="grid grid-cols-3 gap-3">
          <input
            type="text"
            value={formData.location.city}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              location: { ...prev.location, city: e.target.value }
            }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="City"
          />
          <input
            type="text"
            value={formData.location.state}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              location: { ...prev.location, state: e.target.value }
            }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="State"
          />
          <div className="relative">
            <input
              type="number"
              value={formData.location.serviceRadius}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                location: { ...prev.location, serviceRadius: e.target.value }
              }))}
              className="w-full pl-3 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="25"
            />
            <span className="absolute right-3 top-2.5 text-sm text-gray-500">miles</span>
          </div>
        </div>
      </div>

      {/* Optional Business Verification */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start">
          <Shield className="text-blue-600 mr-3 mt-0.5" size={20} />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">Business Verification (Optional)</h4>
            <p className="text-sm text-gray-600 mt-1">
              Verified vendors get a trust badge and appear higher in search results
            </p>
            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business License Number
                </label>
                <input
                  type="text"
                  value={formData.businessLicense}
                  onChange={(e) => setFormData(prev => ({ ...prev, businessLicense: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Insurance Provider
                </label>
                <input
                  type="text"
                  value={formData.insuranceInfo}
                  onChange={(e) => setFormData(prev => ({ ...prev, insuranceInfo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <Package size={48} className="mx-auto text-blue-600 mb-3" />
        <h2 className="text-2xl font-bold">Products & Pricing</h2>
        <p className="text-gray-600 mt-2">Set up your products and bulk pricing tiers</p>
      </div>

      {/* Products */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-gray-700">
            Products/Services *
          </label>
          <button
            type="button"
            onClick={addProduct}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
          >
            <Plus size={16} className="mr-1" />
            Add Product
          </button>
        </div>
        
        {errors.products && (
          <p className="text-red-500 text-sm mb-2">{errors.products}</p>
        )}

        <div className="space-y-3">
          {formData.products.map((product, index) => (
            <div key={product.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Product #{index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeProduct(product.id)}
                  className="text-red-500 hover:text-red-600"
                >
                  <X size={18} />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                    value={product.name}
                    onChange={(e) => updateProduct(product.id, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Product name"
                  />
                </div>
                <div>
                  <select
                    value={product.category}
                    onChange={(e) => updateProduct(product.id, 'category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select category</option>
                    {productCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                    <input
                      type="number"
                      value={product.basePrice}
                      onChange={(e) => updateProduct(product.id, 'basePrice', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Base price"
                      step="0.01"
                    />
                  </div>
                </div>
                <div>
                  <select
                    value={product.unit}
                    onChange={(e) => updateProduct(product.id, 'unit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="each">per unit</option>
                    <option value="pound">per pound</option>
                    <option value="dozen">per dozen</option>
                    <option value="case">per case</option>
                  </select>
                </div>
              </div>
              
              <textarea
                value={product.description}
                onChange={(e) => updateProduct(product.id, 'description', e.target.value)}
                className="w-full mt-3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief product description"
                rows={2}
              />
            </div>
          ))}
          
          {formData.products.length === 0 && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Package size={32} className="mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">No products added yet</p>
              <button
                type="button"
                onClick={addProduct}
                className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Add your first product
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Tiers */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <label className="text-sm font-medium text-gray-700">
              Bulk Pricing Tiers *
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Offer discounts based on order quantity
            </p>
          </div>
          <button
            type="button"
            onClick={addPricingTier}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
          >
            <Plus size={16} className="mr-1" />
            Add Tier
          </button>
        </div>

        {errors.pricingTiers && (
          <p className="text-red-500 text-sm mb-2">{errors.pricingTiers}</p>
        )}

        <div className="space-y-2">
          {formData.pricingTiers.map((tier, index) => (
            <div key={tier.id} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
              <span className="text-sm font-medium text-gray-600 w-16">
                Tier {index + 1}
              </span>
              <input
                type="number"
                value={tier.minQuantity}
                onChange={(e) => updatePricingTier(tier.id, 'minQuantity', e.target.value)}
                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="Min qty"
              />
              <span className="text-gray-500">-</span>
              <input
                type="number"
                value={tier.maxQuantity}
                onChange={(e) => updatePricingTier(tier.id, 'maxQuantity', e.target.value)}
                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="Max qty"
              />
              <div className="relative flex-1">
                <input
                  type="number"
                  value={tier.discount}
                  onChange={(e) => updatePricingTier(tier.id, 'discount', e.target.value)}
                  className="w-full pl-3 pr-8 py-1 border border-gray-300 rounded text-sm"
                  placeholder="Discount"
                />
                <span className="absolute right-2 top-1 text-gray-500">%</span>
              </div>
              <button
                type="button"
                onClick={() => removePricingTier(tier.id)}
                className="text-red-500 hover:text-red-600"
              >
                <X size={16} />
              </button>
            </div>
          ))}

          {formData.pricingTiers.length === 0 && (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <TrendingUp size={24} className="mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                Add pricing tiers to offer bulk discounts
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Minimum Order */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Minimum Order Value *
        </label>
        <div className="relative">
          <span className="absolute left-3 top-2.5 text-gray-500">$</span>
          <input
            type="number"
            value={formData.minimumOrderValue}
            onChange={(e) => setFormData(prev => ({ ...prev, minimumOrderValue: e.target.value }))}
            className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.minimumOrderValue ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="0.00"
            step="0.01"
          />
        </div>
        {errors.minimumOrderValue && (
          <p className="text-red-500 text-sm mt-1">{errors.minimumOrderValue}</p>
        )}
      </div>

      {/* Delivery Options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Delivery Options
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.deliveryOptions.pickup}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                deliveryOptions: { ...prev.deliveryOptions, pickup: e.target.checked }
              }))}
              className="rounded text-blue-600"
            />
            <span className="ml-2 text-sm">Customer pickup available</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.deliveryOptions.delivery}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                deliveryOptions: { ...prev.deliveryOptions, delivery: e.target.checked }
              }))}
              className="rounded text-blue-600"
            />
            <span className="ml-2 text-sm">Local delivery available</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.deliveryOptions.shipping}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                deliveryOptions: { ...prev.deliveryOptions, shipping: e.target.checked }
              }))}
              className="rounded text-blue-600"
            />
            <span className="ml-2 text-sm">Shipping available</span>
          </label>
        </div>
      </div>

      {/* Job Opportunities */}
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex items-start">
          <Users className="text-blue-600 mr-3 mt-0.5" size={20} />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">Need help with fulfillment?</h4>
            <p className="text-sm text-gray-600 mt-1">
              Post logistics jobs to find qualified {updateTerminology('members')} who can help with:
            </p>
            <ul className="text-sm text-gray-600 mt-2 space-y-1">
              <li>• Order packing and preparation</li>
              <li>• Delivery driving</li>
              <li>• Event coordination</li>
              <li>• Inventory management</li>
            </ul>
            <div className="mt-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded text-blue-600"
                  defaultChecked
                />
                <span className="ml-2 text-sm font-medium">
                  I'm interested in posting logistics jobs
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <CheckCircle size={48} className="mx-auto text-green-600 mb-3" />
        <h2 className="text-2xl font-bold">Ready to Launch!</h2>
        <p className="text-gray-600 mt-2">Review terms and start selling to your community</p>
      </div>

      {/* Platform Fee Structure */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start">
          <DollarSign className="text-green-600 mr-3 mt-0.5" size={20} />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">Simple, Transparent Pricing</h4>
            <div className="mt-3 space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-600">Platform Fee</span>
                <span className="font-medium">7.5% per transaction</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-600">Community Logistics</span>
                <span className="font-medium">7.5% (goes to workers)</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">You Keep</span>
                <span className="font-medium text-green-600">85% of each sale</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Setup */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How would you like to receive payments?
        </label>
        <div className="space-y-2">
          <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="paymentMethod"
              value="bank_transfer"
              checked={formData.paymentMethod === 'bank_transfer'}
              onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
              className="text-blue-600"
            />
            <div className="ml-3">
              <span className="text-sm font-medium">Direct Bank Transfer</span>
              <p className="text-xs text-gray-500">Receive payments directly to your bank account</p>
            </div>
          </label>
          <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="paymentMethod"
              value="check"
              checked={formData.paymentMethod === 'check'}
              onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
              className="text-blue-600"
            />
            <div className="ml-3">
              <span className="text-sm font-medium">Check</span>
              <p className="text-xs text-gray-500">Receive paper checks weekly</p>
            </div>
          </label>
        </div>
      </div>

      {/* Success Checklist */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Your Success Checklist</h4>
        <div className="space-y-2">
          <div className="flex items-center text-sm">
            <CheckCircle size={16} className="text-green-600 mr-2" />
            <span>Professional business information added</span>
          </div>
          <div className="flex items-center text-sm">
            <CheckCircle size={16} className="text-green-600 mr-2" />
            <span>{formData.products.length} product{formData.products.length !== 1 ? 's' : ''} ready to sell</span>
          </div>
          <div className="flex items-center text-sm">
            <CheckCircle size={16} className="text-green-600 mr-2" />
            <span>{formData.pricingTiers.length} bulk pricing tier{formData.pricingTiers.length !== 1 ? 's' : ''} configured</span>
          </div>
          <div className="flex items-center text-sm">
            <CheckCircle size={16} className="text-green-600 mr-2" />
            <span>Delivery options selected</span>
          </div>
        </div>
      </div>

      {/* Terms Acceptance */}
      <div className="space-y-3">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={formData.acceptTerms}
            onChange={(e) => setFormData(prev => ({ ...prev, acceptTerms: e.target.checked }))}
            className="rounded text-blue-600 mt-1"
          />
          <div className="ml-3">
            <span className="text-sm font-medium">
              I accept the <a href="#" className="text-blue-600 hover:underline">Vendor Terms & Conditions</a>
            </span>
            <p className="text-xs text-gray-500 mt-1">
              Including quality standards and fulfillment requirements
            </p>
          </div>
        </label>
        
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={formData.acceptPlatformFees}
            onChange={(e) => setFormData(prev => ({ ...prev, acceptPlatformFees: e.target.checked }))}
            className="rounded text-blue-600 mt-1"
          />
          <div className="ml-3">
            <span className="text-sm font-medium">
              I understand the platform fee structure
            </span>
            <p className="text-xs text-gray-500 mt-1">
              7.5% platform fee + 7.5% community logistics fee per transaction
            </p>
          </div>
        </label>
      </div>

      {errors.acceptTerms && (
        <p className="text-red-500 text-sm">{errors.acceptTerms}</p>
      )}
      {errors.acceptPlatformFees && (
        <p className="text-red-500 text-sm">{errors.acceptPlatformFees}</p>
      )}

      {/* What Happens Next */}
      <div className="bg-green-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">What happens next?</h4>
        <ol className="text-sm text-gray-600 space-y-1">
          <li>1. Your vendor dashboard will be instantly available</li>
          <li>2. Products will appear in relevant group searches</li>
          <li>3. {updateTerminology('Members')} can start placing orders immediately</li>
          <li>4. You'll receive notifications for new orders</li>
          <li>5. Access analytics to track your success</li>
        </ol>
      </div>

      {/* Mobile App Reminder */}
      <div className="bg-gray-50 rounded-lg p-4 flex items-center">
        <HelpCircle className="text-gray-400 mr-3" size={20} />
        <div className="flex-1 text-sm text-gray-600">
          <strong>Pro tip:</strong> Download our mobile app to manage orders on the go
        </div>
      </div>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="text-red-600 mt-0.5" size={16} />
          <p className="text-sm text-red-800">{errors.submit}</p>
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Become a Vendor"
      size="large"
    >
      <div className="max-h-[calc(100vh-200px)] overflow-y-auto px-1">
        {renderProgressBar()}
        {renderCurrentStep()}
        
        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center px-4 py-2 text-gray-700 hover:text-gray-900"
              disabled={loading}
            >
              <ArrowLeft size={18} className="mr-2" />
              Back
            </button>
          ) : (
            <div />
          )}
          
          {currentStep < 3 ? (
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              disabled={loading}
            >
              Next
              <ArrowRight size={18} className="ml-2" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !formData.acceptTerms || !formData.acceptPlatformFees}
              className={`flex items-center px-6 py-2 rounded-lg font-medium ${
                loading || !formData.acceptTerms || !formData.acceptPlatformFees
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin mr-2" size={18} />
                  Creating Account...
                </>
              ) : (
                <>
                  Launch My Vendor Account
                  <CheckCircle size={18} className="ml-2" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default VendorOnboarding;